// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
/**
 * Mt Forge - People Page (Clients & Employees)
 *
 * Founders-only page for managing company clients and employees.
 * Lives in MT Forge (Internal) at /people
 *
 * Features:
 * - Two tabs: Clients and Employees
 * - Client management with search, filter, add/edit/delete
 * - Employee management with all 18 team members pre-populated
 * - Quick actions: Send Onboarding Email, Reset Password, Deactivate
 * - Stats overview for each tab
 * - Founders-only access control
 */

import React, { useState, useMemo } from 'react';
import { useAuth } from '../AuthContext';

// ============================================================
// ACCESS CONTROL — Founders Only
// ============================================================
const FOUNDERS = [
  'rmiskulin@gmail.com',
  'criodtech@gmail.com',
  'R.miskulin@miskulintechnologies.com',
  'J.lutz@miskulintechnologies.com',
  'threewayindustries@gmail.com',
];

// ============================================================
// INDUSTRY OPTIONS (all 22 app verticals)
// ============================================================
const INDUSTRIES = [
  'Agriculture', 'Fleet Management', 'Directional Drilling', 'Auto Dealership',
  'Nursing (US)', 'Nursing (CA)', 'Service/HVAC/Plumbing/Electrical',
  'Cannabis', 'Mining', 'Vac Hauling', 'Beauty & Salon', 'Real Estate',
  'Mechanic Shop', 'Tire Shop', 'Cattle Ranching', 'Wellness & Spa',
  'Insurance Brokerage', 'Internal/Corporate', 'Event Venue', 'Paperwork/Forms',
  'Overhead Doors', 'Other',
];

const APP_OPTIONS = [
  'CropPatrol', 'Forge Fleet', 'Directional Ops', 'LotSync', 'NurseVault', 'NurseVault CA',
  'Vantage Ops', 'BudLedger', 'OreSync', 'VacTraq', 'GlowDesk',
  'ClosingDesk', 'WrenchSync', 'TreadOps', 'HerdLedger', 'VitaSync',
  'PolicyForge', 'MT Forge', 'StageVault', 'FormGenius',
];

const CLIENT_STATUSES = ['Lead', 'Active', 'Churned'];

const EMPLOYEE_ROLES = [
  'CEO', 'VP Operations', 'CTO', 'Developer', 'Designer', 'Sales',
  'Marketing', 'Field Ops', 'Logistics', 'Admin', 'Oilfield', 'Mining',
  'Cannabis', 'Trades', 'Events', 'Equipment', 'Healthcare', 'Real Estate',
  'Other',
];

const DEPARTMENTS = [
  'Executive', 'Operations', 'Engineering', 'Sales & Marketing',
  'Field Operations', 'Administration', 'Design', 'Healthcare',
  'Industry Verticals',
];

const EMPLOYEE_STATUSES = ['Active', 'Probation', 'Terminated'];

// ============================================================
// HELPER FUNCTIONS
// ============================================================
function randomId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

function daysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function formatDate(iso) {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ============================================================
// DEMO DATA: 15 Clients
// ============================================================
const DEMO_CLIENTS = [
  { id: randomId(), companyName: 'Prairie Farms Co.', contactName: 'John Miller', email: 'john@prairiefarms.com', phone: '(307) 555-0101', industry: 'Agriculture', apps: ['CropPatrol'], licenseKey: 'FH-K4M9-A2B7-X3P1', status: 'Active', notes: 'Referred by Tyson. 10 user license.', addedDate: daysAgo(180) },
  { id: randomId(), companyName: 'TransCanada Logistics', contactName: 'Mark Chen', email: 'ops@transcanada-log.com', phone: '(403) 555-0202', industry: 'Fleet Management', apps: ['Forge Fleet'], licenseKey: 'FL-B8K2-D4F6-W9H3', status: 'Active', notes: 'Enterprise deal. 200+ trucks.', addedDate: daysAgo(90) },
  { id: randomId(), companyName: 'Apex Drilling Solutions', contactName: 'Dave Kowalski', email: 'admin@apexdrilling.com', phone: '(780) 555-0303', industry: 'Directional Drilling', apps: ['Directional Ops'], licenseKey: 'DI-P3S7-E9G1-V6J5', status: 'Active', notes: 'Annual plan, 25 users.', addedDate: daysAgo(120) },
  { id: randomId(), companyName: 'Mountain View Hospital', contactName: 'Sarah Lin', email: 'it@mountainviewhosp.com', phone: '(406) 555-0404', industry: 'Nursing (US)', apps: ['NurseVault'], licenseKey: 'NQ-A6B3-G7K1-M4N8', status: 'Active', notes: 'Flagship healthcare client. 100 user enterprise.', addedDate: daysAgo(200) },
  { id: randomId(), companyName: 'Elite HVAC Services', contactName: 'Tom Brewer', email: 'owner@elitehvac.com', phone: '(307) 555-0505', industry: 'Service/HVAC/Plumbing/Electrical', apps: ['Vantage Ops'], licenseKey: 'SC-C9D2-H5J7-P3R6', status: 'Active', notes: 'HVAC vertical. Expanding to 15 users.', addedDate: daysAgo(60) },
  { id: randomId(), companyName: 'Rosebud Cannabis Farms', contactName: 'Che McGuire', email: 'che@rosebudcannabisfarms.ca', phone: '(306) 555-0606', industry: 'Cannabis', apps: ['BudLedger'], licenseKey: 'CB-E4F8-J6L3-Q9S2', status: 'Active', notes: 'Internal partner. Annual license.', addedDate: daysAgo(150) },
  { id: randomId(), companyName: 'Northern Resources Ltd', contactName: 'Rick Samson', email: 'ops@northernresources.com', phone: '(780) 555-0707', industry: 'Mining', apps: ['OreSync'], licenseKey: 'MN-G1H5-K8M2-T6V4', status: 'Active', notes: 'New enterprise deal. 50 users.', addedDate: daysAgo(30) },
  { id: randomId(), companyName: 'Summit Realty Group', contactName: 'Angela Torres', email: 'broker@summitrealty.com', phone: '(307) 555-0808', industry: 'Real Estate', apps: ['ClosingDesk'], licenseKey: 'RE-D5E9-W7Y3-B4F6', status: 'Active', notes: '10 agents on annual plan.', addedDate: daysAgo(100) },
  { id: randomId(), companyName: 'BigSky Cattle Co.', contactName: 'Hank Dawson', email: 'ranch@bigskycattle.com', phone: '(406) 555-0909', industry: 'Cattle Ranching', apps: ['HerdLedger'], licenseKey: 'CT-L8M4-E2G5-H9J3', status: 'Active', notes: 'Renewed once. 10 user license.', addedDate: daysAgo(210) },
  { id: randomId(), companyName: 'Alberta Health Services', contactName: 'Linda Park', email: 'procurement@ahs.ca', phone: '(780) 555-1010', industry: 'Nursing (CA)', apps: ['NurseVault CA'], licenseKey: 'NC-C5D9-M3P6-U8W2', status: 'Active', notes: 'Canadian healthcare. 25 users.', addedDate: daysAgo(45) },
  { id: randomId(), companyName: 'Grand Ballroom Events', contactName: 'Michelle Green', email: 'events@grandballroom.com', phone: '(307) 555-1111', industry: 'Event Venue', apps: ['StageVault'], licenseKey: 'EV-S8T2-H4J6-M9P3', status: 'Active', notes: 'Annual plan. 10 users.', addedDate: daysAgo(50) },
  { id: randomId(), companyName: 'Glow Studio', contactName: 'Priya Shah', email: 'owner@glowstudio.com', phone: '(307) 555-1212', industry: 'Beauty & Salon', apps: ['GlowDesk'], licenseKey: 'BQ-S4T8-V6X3-A9C2', status: 'Lead', notes: 'Trial started. Follow up for conversion.', addedDate: daysAgo(10) },
  { id: randomId(), companyName: 'Zen Wellness Center', contactName: 'Amy Brooks', email: 'info@zenwellness.com', phone: '(406) 555-1313', industry: 'Wellness & Spa', apps: ['VitaSync'], licenseKey: 'WL-N5P9-F3H7-K6M2', status: 'Lead', notes: 'Trial started this week.', addedDate: daysAgo(7) },
  { id: randomId(), companyName: 'Shield Insurance', contactName: 'Robert Neal', email: 'agent@shieldins.com', phone: '(307) 555-1414', industry: 'Insurance Brokerage', apps: ['PolicyForge'], licenseKey: 'IB-Q7R3-G5J8-L4N6', status: 'Churned', notes: 'Revoked due to non-payment.', addedDate: daysAgo(120) },
  { id: randomId(), companyName: 'Valley Auto Group', contactName: 'Steve Donovan', email: 'sales@valleyauto.com', phone: '(406) 555-1515', industry: 'Auto Dealership', apps: ['LotSync'], licenseKey: 'MO-T5U9-F2H4-Y8L6', status: 'Churned', notes: 'License expired. Needs renewal follow-up.', addedDate: daysAgo(90) },
];

// ============================================================
// DEMO DATA: 18 Team Members
// ============================================================
const DEMO_EMPLOYEES = [
  { id: randomId(), fullName: 'Ryan Miskulin', personalEmail: 'rmiskulin@gmail.com', companyEmail: 'R.miskulin@miskulintechnologies.com', role: 'CEO', department: 'Executive', startDate: '2024-01-01', phone: '', address: 'Chestermere, AB', status: 'Active', notes: 'Founder & CEO' },
  { id: randomId(), fullName: 'Jordan Lutz', personalEmail: 'J.lutz@miskulintechnologies.com', companyEmail: 'J.lutz@miskulintechnologies.com', role: 'VP Operations', department: 'Executive', startDate: '2024-03-01', phone: '', address: '', status: 'Active', notes: 'Co-Founder' },
  { id: randomId(), fullName: 'Taylor Schenher', personalEmail: 'threewayindustries@gmail.com', companyEmail: 'T.schenher@miskulintechnologies.com', role: 'Logistics', department: 'Operations', startDate: '2024-06-01', phone: '', address: '', status: 'Active', notes: 'Co-Founder' },
  { id: randomId(), fullName: 'Dallas Baschynski', personalEmail: 'desbachynski@gmail.com', companyEmail: 'D.baschynski@miskulintechnologies.com', role: 'Field Ops', department: 'Field Operations', startDate: '2024-06-15', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Lucas Mason', personalEmail: 'lucas.s.mason@gmail.com', companyEmail: 'L.mason@miskulintechnologies.com', role: 'Developer', department: 'Engineering', startDate: '2024-07-01', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Lee Mehling', personalEmail: 'leemehling@hotmail.com', companyEmail: 'L.mehling@miskulintechnologies.com', role: 'Oilfield', department: 'Industry Verticals', startDate: '2024-07-15', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Carmen Loricer', personalEmail: 'cloricer77@gmail.com', companyEmail: 'C.loricer@miskulintechnologies.com', role: 'Admin', department: 'Administration', startDate: '2024-08-01', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Tyson Falk', personalEmail: 'tyson@canstorr.com', companyEmail: 'T.falk@miskulintechnologies.com', role: 'Sales', department: 'Sales & Marketing', startDate: '2024-08-15', phone: '', address: '', status: 'Active', notes: 'Canstorr partnership' },
  { id: randomId(), fullName: 'Tyler Hodgson', personalEmail: 'tyler.hodgson32@gmail.com', companyEmail: 'T.hodgson@miskulintechnologies.com', role: 'Field Ops', department: 'Field Operations', startDate: '2024-09-01', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Jeff Hansen', personalEmail: 'jhansen860@yahoo.com', companyEmail: 'J.hansen@miskulintechnologies.com', role: 'Trades', department: 'Industry Verticals', startDate: '2024-09-15', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Derek Littles', personalEmail: 'littlesderek@gmail.com', companyEmail: 'D.littles@miskulintechnologies.com', role: 'Mining', department: 'Industry Verticals', startDate: '2024-10-01', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Laken Weston', personalEmail: 'laken.weston@live.ca', companyEmail: 'L.weston@miskulintechnologies.com', role: 'Designer', department: 'Design', startDate: '2024-10-15', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Che McGuire', personalEmail: 'che@rosebudcannabisfarms.ca', companyEmail: 'C.mcguire@miskulintechnologies.com', role: 'Cannabis', department: 'Industry Verticals', startDate: '2024-11-01', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Shanon Falk', personalEmail: 'shanon@lanewaymedia.ca', companyEmail: 'S.falk@miskulintechnologies.com', role: 'Marketing', department: 'Sales & Marketing', startDate: '2024-11-15', phone: '', address: '', status: 'Active', notes: 'Laneway Media' },
  { id: randomId(), fullName: 'Amanda Falk', personalEmail: 'amandakristaleigh@hotmail.com', companyEmail: 'A.falk@miskulintechnologies.com', role: 'Events', department: 'Industry Verticals', startDate: '2024-12-01', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Darren Blahut', personalEmail: 'dblahut@sastel.net', companyEmail: 'D.blahut@miskulintechnologies.com', role: 'Equipment', department: 'Field Operations', startDate: '2025-01-01', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Suzanne Spink', personalEmail: '', companyEmail: 'S.spink@miskulintechnologies.com', role: 'Healthcare', department: 'Healthcare', startDate: '2025-01-15', phone: '', address: '', status: 'Active', notes: '' },
  { id: randomId(), fullName: 'Jessica Flaig', personalEmail: '', companyEmail: 'J.flaig@miskulintechnologies.com', role: 'Real Estate', department: 'Industry Verticals', startDate: '2025-02-01', phone: '', address: '', status: 'Active', notes: '' },
];

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function PeoplePage() {
  const { user } = useAuth();

  // Access check
  const isFounder = user && FOUNDERS.includes(user.email);

  // Tab state
  const [activeTab, setActiveTab] = useState('clients');

  // Client state
  const [clients, setClients] = useState(DEMO_CLIENTS);
  const [clientSearch, setClientSearch] = useState('');
  const [clientFilterIndustry, setClientFilterIndustry] = useState('');
  const [clientFilterStatus, setClientFilterStatus] = useState('');
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    companyName: '', contactName: '', email: '', phone: '',
    industry: '', apps: [], licenseKey: '', status: 'Lead', notes: '',
  });

  // Employee state
  const [employees, setEmployees] = useState(DEMO_EMPLOYEES);
  const [empSearch, setEmpSearch] = useState('');
  const [empFilterStatus, setEmpFilterStatus] = useState('');
  const [empFilterDept, setEmpFilterDept] = useState('');
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState({
    fullName: '', personalEmail: '', companyEmail: '', role: '',
    department: '', startDate: '', phone: '', address: '',
    status: 'Active', notes: '',
  });

  // Notification
  const [notification, setNotification] = useState(null);

  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // ============================================================
  // CLIENT LOGIC
  // ============================================================
  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      if (clientFilterIndustry && c.industry !== clientFilterIndustry) return false;
      if (clientFilterStatus && c.status !== clientFilterStatus) return false;
      if (clientSearch) {
        const q = clientSearch.toLowerCase();
        return (
          c.companyName.toLowerCase().includes(q) ||
          c.contactName.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.industry.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [clients, clientSearch, clientFilterIndustry, clientFilterStatus]);

  const clientStats = useMemo(() => {
    const total = clients.length;
    const active = clients.filter(c => c.status === 'Active').length;
    const leads = clients.filter(c => c.status === 'Lead').length;
    // Estimate: $499/yr per active client as baseline
    const revenue = active * 499;
    return { total, active, leads, revenue };
  }, [clients]);

  const openAddClient = () => {
    setEditingClient(null);
    setClientForm({
      companyName: '', contactName: '', email: '', phone: '',
      industry: '', apps: [], licenseKey: '', status: 'Lead', notes: '',
    });
    setShowClientModal(true);
  };

  const openEditClient = (client) => {
    setEditingClient(client.id);
    setClientForm({
      companyName: client.companyName,
      contactName: client.contactName,
      email: client.email,
      phone: client.phone,
      industry: client.industry,
      apps: client.apps || [],
      licenseKey: client.licenseKey,
      status: client.status,
      notes: client.notes,
    });
    setShowClientModal(true);
  };

  const saveClient = () => {
    if (!clientForm.companyName.trim()) {
      showNotification('Company name is required.', 'error');
      return;
    }
    if (editingClient) {
      setClients(prev => prev.map(c =>
        c.id === editingClient ? { ...c, ...clientForm } : c
      ));
      showNotification('Client updated successfully.');
    } else {
      const newClient = {
        id: randomId(),
        ...clientForm,
        addedDate: new Date().toISOString().split('T')[0],
      };
      setClients(prev => [newClient, ...prev]);
      showNotification('Client added successfully.');
    }
    setShowClientModal(false);
  };

  const deleteClient = (id) => {
    setClients(prev => prev.filter(c => c.id !== id));
    showNotification('Client removed.');
  };

  // ============================================================
  // EMPLOYEE LOGIC
  // ============================================================
  const filteredEmployees = useMemo(() => {
    return employees.filter(e => {
      if (empFilterStatus && e.status !== empFilterStatus) return false;
      if (empFilterDept && e.department !== empFilterDept) return false;
      if (empSearch) {
        const q = empSearch.toLowerCase();
        return (
          e.fullName.toLowerCase().includes(q) ||
          e.personalEmail.toLowerCase().includes(q) ||
          e.companyEmail.toLowerCase().includes(q) ||
          e.role.toLowerCase().includes(q) ||
          e.department.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [employees, empSearch, empFilterStatus, empFilterDept]);

  const empStats = useMemo(() => {
    const total = employees.length;
    const active = employees.filter(e => e.status === 'Active').length;
    const depts = [...new Set(employees.map(e => e.department))].length;
    return { total, active, depts };
  }, [employees]);

  const openAddEmp = () => {
    setEditingEmp(null);
    setEmpForm({
      fullName: '', personalEmail: '', companyEmail: '', role: '',
      department: '', startDate: '', phone: '', address: '',
      status: 'Active', notes: '',
    });
    setShowEmpModal(true);
  };

  const openEditEmp = (emp) => {
    setEditingEmp(emp.id);
    setEmpForm({
      fullName: emp.fullName,
      personalEmail: emp.personalEmail,
      companyEmail: emp.companyEmail,
      role: emp.role,
      department: emp.department,
      startDate: emp.startDate,
      phone: emp.phone,
      address: emp.address,
      status: emp.status,
      notes: emp.notes,
    });
    setShowEmpModal(true);
  };

  const saveEmp = () => {
    if (!empForm.fullName.trim()) {
      showNotification('Full name is required.', 'error');
      return;
    }
    if (editingEmp) {
      setEmployees(prev => prev.map(e =>
        e.id === editingEmp ? { ...e, ...empForm } : e
      ));
      showNotification('Employee updated successfully.');
    } else {
      const newEmp = {
        id: randomId(),
        ...empForm,
      };
      setEmployees(prev => [newEmp, ...prev]);
      showNotification('Employee added successfully.');
    }
    setShowEmpModal(false);
  };

  const deleteEmp = (id) => {
    setEmployees(prev => prev.filter(e => e.id !== id));
    showNotification('Employee removed.');
  };

  const handleQuickAction = (action, emp) => {
    if (action === 'onboarding') {
      showNotification(`Onboarding email sent to ${emp.personalEmail || emp.companyEmail}`);
    } else if (action === 'reset') {
      showNotification(`Password reset for ${emp.fullName}. Temporary password: Island2026!`);
    } else if (action === 'deactivate') {
      setEmployees(prev => prev.map(e =>
        e.id === emp.id ? { ...e, status: 'Terminated' } : e
      ));
      showNotification(`${emp.fullName} deactivated.`);
    }
  };

  // ============================================================
  // ACCESS RESTRICTED VIEW
  // ============================================================
  if (!isFounder) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
          padding: 48, textAlign: 'center', maxWidth: 440,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', background: '#7f1d1d',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: 28,
          }}>
            !
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', margin: '0 0 8px' }}>
            Access Restricted
          </h2>
          <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>
            Founders Only — This page is restricted to company founders.
          </p>
          <p style={{ color: '#475569', fontSize: 12, marginTop: 12 }}>
            Contact Ryan Miskulin for access.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div>
      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8,
          background: notification.type === 'success' ? '#065f46' : '#991b1b',
          color: '#fff', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          {notification.msg}
        </div>
      )}

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9', margin: 0 }}>People</h1>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>Manage clients and employees across Mt Forge</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {activeTab === 'clients' ? (
            <button onClick={openAddClient} style={{ ...st.btn, ...st.btnPrimary, fontSize: 14, padding: '10px 20px' }}>
              + Add Client
            </button>
          ) : (
            <button onClick={openAddEmp} style={{ ...st.btn, ...st.btnPrimary, fontSize: 14, padding: '10px 20px' }}>
              + Add Employee
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #334155' }}>
        {[
          { key: 'clients', label: 'Clients', count: clients.length },
          { key: 'employees', label: 'Employees', count: employees.length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #4F46E5' : '2px solid transparent',
              color: activeTab === tab.key ? '#f1f5f9' : '#64748b',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: -2,
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
            <span style={{
              marginLeft: 8, padding: '2px 8px', borderRadius: 10,
              background: activeTab === tab.key ? '#4F46E5' : '#334155',
              color: activeTab === tab.key ? '#fff' : '#94a3b8',
              fontSize: 12, fontWeight: 700,
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ============================================================ */}
      {/* CLIENTS TAB */}
      {/* ============================================================ */}
      {activeTab === 'clients' && (
        <div>
          {/* Client Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <div style={st.statCard}>
              <div style={st.statLabel}>Total Clients</div>
              <div style={st.statValue}>{clientStats.total}</div>
            </div>
            <div style={st.statCard}>
              <div style={st.statLabel}>Active</div>
              <div style={{ ...st.statValue, color: '#10b981' }}>{clientStats.active}</div>
            </div>
            <div style={st.statCard}>
              <div style={st.statLabel}>Leads</div>
              <div style={{ ...st.statValue, color: '#f59e0b' }}>{clientStats.leads}</div>
            </div>
            <div style={st.statCard}>
              <div style={st.statLabel}>Est. Monthly Revenue</div>
              <div style={{ ...st.statValue, color: '#10b981' }}>${Math.round(clientStats.revenue / 12).toLocaleString()}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>${clientStats.revenue.toLocaleString()}/yr</div>
            </div>
          </div>

          {/* Client Filters */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search clients..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              style={{ ...st.filterInput, width: 260 }}
            />
            <select value={clientFilterIndustry} onChange={(e) => setClientFilterIndustry(e.target.value)} style={st.filterSelect}>
              <option value="">All Industries</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            <select value={clientFilterStatus} onChange={(e) => setClientFilterStatus(e.target.value)} style={st.filterSelect}>
              <option value="">All Status</option>
              {CLIENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(clientSearch || clientFilterIndustry || clientFilterStatus) && (
              <button
                onClick={() => { setClientSearch(''); setClientFilterIndustry(''); setClientFilterStatus(''); }}
                style={{ ...st.btn, ...st.btnSecondary, padding: '6px 12px', fontSize: 12 }}
              >
                Clear
              </button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
              {filteredClients.length} of {clients.length}
            </span>
          </div>

          {/* Client Table */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
                <thead>
                  <tr>
                    <th style={st.th}>Company</th>
                    <th style={st.th}>Contact</th>
                    <th style={st.th}>Email</th>
                    <th style={st.th}>Industry</th>
                    <th style={st.th}>Apps</th>
                    <th style={st.th}>Status</th>
                    <th style={st.th}>Added</th>
                    <th style={{ ...st.th, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                        No clients found.
                      </td>
                    </tr>
                  ) : (
                    filteredClients.map(client => (
                      <tr key={client.id} style={{ borderBottom: '1px solid rgba(51,65,85,0.5)' }}>
                        <td style={st.td}>
                          <div style={{ fontWeight: 600, color: '#f1f5f9' }}>{client.companyName}</div>
                        </td>
                        <td style={st.td}>
                          <span style={{ color: '#e2e8f0', fontSize: 13 }}>{client.contactName}</span>
                        </td>
                        <td style={st.td}>
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>{client.email}</span>
                        </td>
                        <td style={st.td}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: '#1e1b4b', color: '#a5b4fc',
                          }}>
                            {client.industry}
                          </span>
                        </td>
                        <td style={st.td}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {(client.apps || []).map(app => (
                              <span key={app} style={{
                                padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                background: '#0f172a', color: '#4F46E5', border: '1px solid #334155',
                              }}>
                                {app}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={st.td}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: client.status === 'Active' ? '#10b981' : client.status === 'Lead' ? '#f59e0b' : '#ef4444',
                            color: '#fff',
                          }}>
                            {client.status}
                          </span>
                        </td>
                        <td style={st.td}>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>{formatDate(client.addedDate)}</span>
                        </td>
                        <td style={{ ...st.td, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button onClick={() => openEditClient(client)} style={st.actionBtn}>Edit</button>
                            <button
                              onClick={() => deleteClient(client.id)}
                              style={{ ...st.actionBtn, color: '#ef4444', borderColor: '#7f1d1d' }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* EMPLOYEES TAB */}
      {/* ============================================================ */}
      {activeTab === 'employees' && (
        <div>
          {/* Employee Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
            <div style={st.statCard}>
              <div style={st.statLabel}>Total Employees</div>
              <div style={st.statValue}>{empStats.total}</div>
            </div>
            <div style={st.statCard}>
              <div style={st.statLabel}>Active</div>
              <div style={{ ...st.statValue, color: '#10b981' }}>{empStats.active}</div>
            </div>
            <div style={st.statCard}>
              <div style={st.statLabel}>Departments</div>
              <div style={st.statValue}>{empStats.depts}</div>
            </div>
          </div>

          {/* Employee Filters */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search employees..."
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              style={{ ...st.filterInput, width: 260 }}
            />
            <select value={empFilterDept} onChange={(e) => setEmpFilterDept(e.target.value)} style={st.filterSelect}>
              <option value="">All Departments</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={empFilterStatus} onChange={(e) => setEmpFilterStatus(e.target.value)} style={st.filterSelect}>
              <option value="">All Status</option>
              {EMPLOYEE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(empSearch || empFilterDept || empFilterStatus) && (
              <button
                onClick={() => { setEmpSearch(''); setEmpFilterDept(''); setEmpFilterStatus(''); }}
                style={{ ...st.btn, ...st.btnSecondary, padding: '6px 12px', fontSize: 12 }}
              >
                Clear
              </button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
              {filteredEmployees.length} of {employees.length}
            </span>
          </div>

          {/* Employee Table */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th style={st.th}>Name</th>
                    <th style={st.th}>Email</th>
                    <th style={st.th}>Role</th>
                    <th style={st.th}>Department</th>
                    <th style={st.th}>Start Date</th>
                    <th style={st.th}>Status</th>
                    <th style={{ ...st.th, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                        No employees found.
                      </td>
                    </tr>
                  ) : (
                    filteredEmployees.map(emp => (
                      <tr key={emp.id} style={{ borderBottom: '1px solid rgba(51,65,85,0.5)' }}>
                        <td style={st.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: '50%',
                              background: 'linear-gradient(135deg, #4F46E5, #7c3aed)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
                            }}>
                              {emp.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 13 }}>{emp.fullName}</div>
                              {emp.companyEmail && (
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{emp.companyEmail}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={st.td}>
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>{emp.personalEmail || '--'}</span>
                        </td>
                        <td style={st.td}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: '#1e1b4b', color: '#a5b4fc',
                          }}>
                            {emp.role}
                          </span>
                        </td>
                        <td style={st.td}>
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>{emp.department}</span>
                        </td>
                        <td style={st.td}>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>{formatDate(emp.startDate)}</span>
                        </td>
                        <td style={st.td}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                            background: emp.status === 'Active' ? '#10b981' : emp.status === 'Probation' ? '#f59e0b' : '#ef4444',
                            color: '#fff',
                          }}>
                            {emp.status}
                          </span>
                        </td>
                        <td style={{ ...st.td, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => handleQuickAction('onboarding', emp)} style={st.actionBtn} title="Send onboarding email">
                              Onboard
                            </button>
                            <button onClick={() => handleQuickAction('reset', emp)} style={st.actionBtn} title="Reset password">
                              Reset PW
                            </button>
                            <button onClick={() => openEditEmp(emp)} style={st.actionBtn}>Edit</button>
                            {emp.status === 'Active' ? (
                              <button
                                onClick={() => handleQuickAction('deactivate', emp)}
                                style={{ ...st.actionBtn, color: '#ef4444', borderColor: '#7f1d1d' }}
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => deleteEmp(emp.id)}
                                style={{ ...st.actionBtn, color: '#ef4444', borderColor: '#7f1d1d' }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: Add/Edit Client */}
      {/* ============================================================ */}
      {showClientModal && (
        <div style={st.modalOverlay} onClick={() => setShowClientModal(false)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
                {editingClient ? 'Edit Client' : 'Add Client'}
              </h2>
              <button onClick={() => setShowClientModal(false)} style={st.modalClose}>X</button>
            </div>
            <div style={st.modalBody}>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>Company Name *</label>
                  <input
                    type="text" value={clientForm.companyName}
                    onChange={(e) => setClientForm(p => ({ ...p, companyName: e.target.value }))}
                    placeholder="Acme Corp" style={st.formInput}
                  />
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>Contact Name</label>
                  <input
                    type="text" value={clientForm.contactName}
                    onChange={(e) => setClientForm(p => ({ ...p, contactName: e.target.value }))}
                    placeholder="John Smith" style={st.formInput}
                  />
                </div>
              </div>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>Email</label>
                  <input
                    type="email" value={clientForm.email}
                    onChange={(e) => setClientForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="contact@company.com" style={st.formInput}
                  />
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>Phone</label>
                  <input
                    type="text" value={clientForm.phone}
                    onChange={(e) => setClientForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="(307) 555-0000" style={st.formInput}
                  />
                </div>
              </div>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>Industry</label>
                  <select
                    value={clientForm.industry}
                    onChange={(e) => setClientForm(p => ({ ...p, industry: e.target.value }))}
                    style={st.formInput}
                  >
                    <option value="">Select Industry</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>Status</label>
                  <select
                    value={clientForm.status}
                    onChange={(e) => setClientForm(p => ({ ...p, status: e.target.value }))}
                    style={st.formInput}
                  >
                    {CLIENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>App(s) Using</label>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !clientForm.apps.includes(e.target.value)) {
                        setClientForm(p => ({ ...p, apps: [...p.apps, e.target.value] }));
                      }
                    }}
                    style={st.formInput}
                  >
                    <option value="">Add an app...</option>
                    {APP_OPTIONS.filter(a => !clientForm.apps.includes(a)).map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                  {clientForm.apps.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      {clientForm.apps.map(app => (
                        <span key={app} style={{
                          padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          background: '#0f172a', color: '#4F46E5', border: '1px solid #334155',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          {app}
                          <span
                            onClick={() => setClientForm(p => ({ ...p, apps: p.apps.filter(a => a !== app) }))}
                            style={{ cursor: 'pointer', color: '#ef4444', fontWeight: 800, marginLeft: 2 }}
                          >
                            x
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>License Key</label>
                  <input
                    type="text" value={clientForm.licenseKey}
                    onChange={(e) => setClientForm(p => ({ ...p, licenseKey: e.target.value }))}
                    placeholder="XX-XXXX-XXXX-XXXX" style={st.formInput}
                  />
                </div>
              </div>
              <div style={st.formField}>
                <label style={st.formLabel}>Notes</label>
                <textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Notes about this client..."
                  rows={3}
                  style={{ ...st.formInput, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div style={st.modalFooter}>
              <button onClick={() => setShowClientModal(false)} style={{ ...st.btn, ...st.btnSecondary }}>Cancel</button>
              <button onClick={saveClient} style={{ ...st.btn, ...st.btnPrimary }}>
                {editingClient ? 'Save Changes' : 'Add Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: Add/Edit Employee */}
      {/* ============================================================ */}
      {showEmpModal && (
        <div style={st.modalOverlay} onClick={() => setShowEmpModal(false)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
                {editingEmp ? 'Edit Employee' : 'Add Employee'}
              </h2>
              <button onClick={() => setShowEmpModal(false)} style={st.modalClose}>X</button>
            </div>
            <div style={st.modalBody}>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>Full Name *</label>
                  <input
                    type="text" value={empForm.fullName}
                    onChange={(e) => setEmpForm(p => ({ ...p, fullName: e.target.value }))}
                    placeholder="Jane Doe" style={st.formInput}
                  />
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>Role</label>
                  <select
                    value={empForm.role}
                    onChange={(e) => setEmpForm(p => ({ ...p, role: e.target.value }))}
                    style={st.formInput}
                  >
                    <option value="">Select Role</option>
                    {EMPLOYEE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>Personal Email</label>
                  <input
                    type="email" value={empForm.personalEmail}
                    onChange={(e) => setEmpForm(p => ({ ...p, personalEmail: e.target.value }))}
                    placeholder="jane@gmail.com" style={st.formInput}
                  />
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>Company Email</label>
                  <input
                    type="email" value={empForm.companyEmail}
                    onChange={(e) => setEmpForm(p => ({ ...p, companyEmail: e.target.value }))}
                    placeholder="J.doe@miskulintechnologies.com" style={st.formInput}
                  />
                </div>
              </div>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>Department</label>
                  <select
                    value={empForm.department}
                    onChange={(e) => setEmpForm(p => ({ ...p, department: e.target.value }))}
                    style={st.formInput}
                  >
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>Start Date</label>
                  <input
                    type="date" value={empForm.startDate}
                    onChange={(e) => setEmpForm(p => ({ ...p, startDate: e.target.value }))}
                    style={st.formInput}
                  />
                </div>
              </div>
              <div style={st.formRow}>
                <div style={st.formField}>
                  <label style={st.formLabel}>Phone</label>
                  <input
                    type="text" value={empForm.phone}
                    onChange={(e) => setEmpForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="(307) 555-0000" style={st.formInput}
                  />
                </div>
                <div style={st.formField}>
                  <label style={st.formLabel}>Status</label>
                  <select
                    value={empForm.status}
                    onChange={(e) => setEmpForm(p => ({ ...p, status: e.target.value }))}
                    style={st.formInput}
                  >
                    {EMPLOYEE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={st.formField}>
                <label style={st.formLabel}>Address</label>
                <input
                  type="text" value={empForm.address}
                  onChange={(e) => setEmpForm(p => ({ ...p, address: e.target.value }))}
                  placeholder="City, State" style={st.formInput}
                />
              </div>
              <div style={st.formField}>
                <label style={st.formLabel}>Notes</label>
                <textarea
                  value={empForm.notes}
                  onChange={(e) => setEmpForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Notes about this employee..."
                  rows={3}
                  style={{ ...st.formInput, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div style={st.modalFooter}>
              <button onClick={() => setShowEmpModal(false)} style={{ ...st.btn, ...st.btnSecondary }}>Cancel</button>
              <button onClick={saveEmp} style={{ ...st.btn, ...st.btnPrimary }}>
                {editingEmp ? 'Save Changes' : 'Add Employee'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// INLINE STYLES
// ============================================================
const st = {
  btn: {
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.15s',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #4F46E5, #7c3aed)',
    color: '#fff',
  },
  btnSecondary: {
    background: '#1e293b',
    color: '#e2e8f0',
    border: '1px solid #334155',
  },
  statCard: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 20,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 600,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 800,
    color: '#f1f5f9',
    marginTop: 4,
  },
  filterInput: {
    padding: '8px 12px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 13,
    outline: 'none',
  },
  filterSelect: {
    padding: '8px 12px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 13,
    outline: 'none',
  },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '1px solid #334155',
    background: '#0f172a',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    color: '#e2e8f0',
    borderBottom: '1px solid rgba(51,65,85,0.5)',
    verticalAlign: 'middle',
  },
  actionBtn: {
    padding: '4px 10px',
    background: 'none',
    border: '1px solid #334155',
    color: '#94a3b8',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 16,
    width: 680,
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #334155',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: 18,
    cursor: 'pointer',
    padding: '4px 8px',
    fontWeight: 700,
  },
  modalBody: {
    padding: 24,
  },
  modalFooter: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #334155',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 0,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#94a3b8',
  },
  formInput: {
    padding: '10px 14px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 8,
    color: '#e2e8f0',
    fontSize: 14,
    outline: 'none',
  },
};
